import {
  FlexColumn,
  FlexRow,
  LabeledInput,
  LabeledTextArea,
  StyledButton,
  useLoading,
} from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { useEffect, useMemo, useState } from 'react';
import { areEqual } from 'react-window';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import {
  CUSTOMER_ID,
  CUSTOMER_NAME,
  EMPTY_WEB_SERVICE,
  HEADERS,
  METHOD,
  NEW_WSDL_PASSWORD,
  WS_TYPE_LITE_QUALITAS,
  WS_TYPE_RECURSO_CONFIABLE,
  WS_TYPE_UNIGIS,
  WS_TYPE_WEBHOOK,
  WSDL_URL,
  WSDL_USER,
} from '../../../constants/webService';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { createWebService, updateWebService } from '../../../services/webServiceService';
import { SubClientCore } from '../../../types/subClient';
import {
  MinimalWebService,
  RecursoConfiable,
  WebhookData,
  WebService,
  WebServiceDataCore,
} from '../../../types/webService';
import { WebServiceTypePicker } from '../../shared/WebServiceTypePicker';
import { AssetListPicker } from '../../shared/assetListPicker/AssetListPicker';

type Props = {
  subClientId: string;
  webService?: WebService;
  onCancel: () => void;
  afterSave: () => void;
};
export function WebServiceForm({ webService, subClientId, onCancel, afterSave }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const [editingWebService, setEditingWebService] = useState<MinimalWebService>(EMPTY_WEB_SERVICE);
  const [jsonStringValue, setJsonStringValue] = useState<string>('{}');
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();

  useEffect(() => {
    if (webService) {
      setEditingWebService(webService);
      try {
        setJsonStringValue(JSON.stringify((webService.data as WebhookData)?.HEADERS) ?? '{}');
      } catch {
        setJsonStringValue('{}');
      }
    } else if (subClientId) {
      setEditingWebService((prev) => ({
        ...prev,
        subClient: { _id: subClientId } as SubClientCore,
      }));
    }
  }, [webService, subClientId]);

  useUpdateEffect(() => {
    if (webService) {
      setEditingWebService((prev) => ({ ...prev, data: webService.data }));
      try {
        setJsonStringValue(JSON.stringify((webService.data as WebhookData)?.HEADERS) ?? '{}');
      } catch {
        setJsonStringValue('{}');
      }
    } else if (editingWebService.wsType === WS_TYPE_WEBHOOK) {
      setEditingWebService((prev) => ({ ...prev, data: { WSDL_URL: '', METHOD: 'POST', HEADERS: {} } }));
    } else {
      setEditingWebService((prev) => ({ ...prev, data: EMPTY_WEB_SERVICE.data }));
    }
  }, [editingWebService.wsType]);

  const hasUpdates = !areEqual(webService ?? {}, editingWebService);

  const isValidJson = useMemo(() => {
    try {
      JSON.parse(jsonStringValue);
      return true;
    } catch {
      return false;
    }
  }, [jsonStringValue]);

  useEffect(() => {
    if (isValidJson) {
      onDataChange(JSON.parse(jsonStringValue), HEADERS);
    }
    onDataChange({}, HEADERS);
  }, [jsonStringValue]);

  const canSubmit = useMemo(() => {
    if (!isValidJson) return false;
    if (!editingWebService.name || !editingWebService.wsType) return false;
    else if (editingWebService.wsType === WS_TYPE_WEBHOOK) {
      const data = editingWebService.data as WebhookData;
      if (!!data?.WSDL_URL && !!data?.METHOD && !!data?.HEADERS) {
        return true;
      }
    } else if (editingWebService.wsType === WS_TYPE_LITE_QUALITAS) {
      return true;
    } else if (!webService?._id && editingWebService.data && !('NEW_WSDL_PASSWORD' in editingWebService.data)) {
      return false;
    } else if (editingWebService.wsType === WS_TYPE_UNIGIS) {
      const data = editingWebService.data as WebServiceDataCore;
      if (!!data?.WSDL_URL && !!data?.WSDL_USER) {
        return true;
      }
    } else if (editingWebService.wsType === WS_TYPE_RECURSO_CONFIABLE) {
      const data = editingWebService.data as RecursoConfiable;
      if (!!data?.CUSTOMER_ID && !!data?.CUSTOMER_NAME && !!data?.WSDL_URL && !!data?.WSDL_USER) {
        return true;
      }
    }
    return false;
  }, [editingWebService]);
  const onInputChange = (value: any, field: keyof MinimalWebService) => {
    setEditingWebService((prev) => ({ ...prev, [field]: value }));
  };
  const onDataChange = (value: any, field: keyof WebServiceDataCore | keyof RecursoConfiable | keyof WebhookData) => {
    setEditingWebService((prev) => ({
      ...prev,
      data: { ...prev.data, [field]: value } as WebServiceDataCore | RecursoConfiable | WebhookData,
    }));
  };

  const onSave = async () => {
    startLoading();
    const { id, error } = webService
      ? await updateWebService(editingWebService)
      : await createWebService(editingWebService);
    if (!error && id) {
      afterSave();
      showPositiveFeedback(
        webService
          ? FEEDBACK.edited(FEEDBACK_PREFIXES.webService, editingWebService.name)
          : FEEDBACK.created(FEEDBACK_PREFIXES.webService),
      );
    } else {
      showFailFeedback(
        error ||
          (webService
            ? FEEDBACK.failedEdition(FEEDBACK_PREFIXES.webService, editingWebService.name)
            : FEEDBACK.failedCreation(FEEDBACK_PREFIXES.webService)),
      );
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
      })}
    >
      <FlexColumn
        classNames={css({
          overflowY: 'auto',
          overflowX: 'hidden',
          height: '100%',
        })}
      >
        <LabeledInput
          label="Nombre:"
          value={editingWebService.name}
          onChange={(value) => onInputChange(value, 'name')}
          required
        />
        <WebServiceTypePicker
          onChange={(wsType) => onInputChange(wsType, 'wsType')}
          wsType={editingWebService.wsType}
          required
        />
        {editingWebService.wsType === WS_TYPE_RECURSO_CONFIABLE && (
          <>
            <LabeledInput
              label={CUSTOMER_ID}
              value={(editingWebService.data as RecursoConfiable)?.CUSTOMER_ID ?? ''}
              onChange={(value) => onDataChange(value, CUSTOMER_ID)}
              required
            />
            <LabeledInput
              label={CUSTOMER_NAME}
              value={(editingWebService.data as RecursoConfiable)?.CUSTOMER_NAME ?? ''}
              onChange={(value) => onDataChange(value, CUSTOMER_NAME)}
              required
            />
          </>
        )}
        {editingWebService.wsType === WS_TYPE_WEBHOOK && (
          <>
            <LabeledInput
              label={WSDL_URL}
              value={(editingWebService.data as WebhookData)?.WSDL_URL ?? ''}
              onChange={(value) => onDataChange(value, WSDL_URL)}
              required
            />
            <LabeledInput
              label={METHOD}
              value={(editingWebService.data as WebhookData)?.METHOD ?? ''}
              onChange={(value) => onDataChange(value, METHOD)}
              readonly
              required
            />
            <LabeledTextArea
              label={HEADERS}
              value={jsonStringValue}
              onChangeValue={setJsonStringValue}
              required
              error={!isValidJson}
            />
          </>
        )}
        {![undefined, WS_TYPE_WEBHOOK, WS_TYPE_LITE_QUALITAS].includes(editingWebService.wsType) && (
          <>
            <LabeledInput
              label={WSDL_URL}
              value={(editingWebService.data as WebServiceDataCore)?.WSDL_URL ?? ''}
              onChange={(value) => onDataChange(value, WSDL_URL)}
              required
            />
            <LabeledInput
              label={WSDL_USER}
              value={(editingWebService.data as WebServiceDataCore)?.WSDL_USER ?? ''}
              onChange={(value) => onDataChange(value, WSDL_USER)}
              required
            />
            <LabeledInput
              type="password"
              label={NEW_WSDL_PASSWORD}
              value={(editingWebService.data as WebServiceDataCore)?.NEW_WSDL_PASSWORD ?? ''}
              onChange={(value) => onDataChange(value, NEW_WSDL_PASSWORD)}
              required={!webService?._id}
            />
          </>
        )}
        <Block
          minHeight={'300px'}
          display={'flex'}
          flexDirection={'column'}
        >
          <AssetListPicker
            subClientId={subClientId}
            assignedAssetIds={editingWebService.assignedAssets}
            onAssignedChange={(assetIds) => onInputChange(assetIds, 'assignedAssets')}
          />
        </Block>
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          {webService?._id && (
            <StyledButton
              kind="tertiary"
              onClick={onCancel}
            >
              Cancelar
            </StyledButton>
          )}
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit || !hasUpdates}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
